import { CHAT_UNREAD_BADGE } from '../messaging/chatUnreadBadgeTheme';
import { theme as antdTheme } from "antd";
import { appThemeConfig } from "../theme/AppTheme";
import React, { useContext } from "react";
import { formatWaTextSegment as _fmtSegment } from "./whatsappTextFormatting";
export { formatWaTextInlineHtml } from "./whatsappTextFormatting";

const sharedDarkTokens = antdTheme.getDesignToken(appThemeConfig(true));

// ── WhatsApp text formatting ──────────────────────────────────────────────────
// Apostrophes are allowed inside links (Google Maps place links carry 25°17'35"N)
// but a trailing quote is treated as punctuation.
const URL_REGEX = /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/gi;


export function WaText({ text, onLinkClick }) {
  const isDark = useContext(ChatThemeCtx);
  const audience = useContext(ChatAudienceCtx);
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    const value = m[0].replace(/'+$/, "");
    parts.push({ type: "link", value });
    last = m.index + value.length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {parts.map((p, i) =>
        p.type === "link" ? (
          <a key={i}
            href={p.value}
            onClick={e => { e.preventDefault(); onLinkClick ? onLinkClick(p.value) : window.open(p.value.startsWith("http") ? p.value : `https://${p.value}`, "_blank", "noopener,noreferrer"); }}
            style={{ color: ct(isDark, audience).link, textDecoration: "underline", cursor: "pointer" }}>
            {p.value}
          </a>
        ) : (
          <span key={i} dangerouslySetInnerHTML={{ __html: _fmtSegment(p.value, isDark) }} />
        )
      )}
    </span>
  );
}

export function formatWaText(text) { return <WaText text={text} />; }

// ── CSS injections ────────────────────────────────────────────────────────────
(() => {
  if (typeof document !== "undefined" && !document.getElementById("msg-status-glow-style")) {
    const s = document.createElement("style");
    s.id = "msg-status-glow-style";
    s.textContent = `
      @keyframes msgStatusGlow {
        0%   { filter: drop-shadow(0 0 0px currentColor); opacity: 1; }
        35%  { filter: drop-shadow(0 0 6px currentColor); opacity: 0.8; }
        100% { filter: drop-shadow(0 0 0px currentColor); opacity: 1; }
      }
      .cpt, .cpt * {
        transition:
          background-color 0.35s ease,
          background 0.35s ease,
          color 0.25s ease,
          border-color 0.35s ease !important;
      }
      @keyframes chatInputLaunch {
        0%   { transform: scale(1)    translateY(0);   opacity: 1; }
        40%  { transform: scale(0.96) translateY(-7px); opacity: 0.5; }
        100% { transform: scale(1)    translateY(0);   opacity: 1; }
      }
      /* Reaction ("add emoji") button next to a message bubble — hidden
         until you hover the message row it belongs to, so it doesn't sit
         there crowding every single bubble at all times. pointer-events
         toggles alongside opacity so the invisible button can't steal
         clicks meant for whatever's actually underneath it. */
      .msg-row .reaction-btn {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;
      }
      .msg-row:hover .reaction-btn {
        opacity: 1;
        pointer-events: auto;
      }
      .chat-input-launch {
        animation: chatInputLaunch 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `;
    document.head.appendChild(s);
  }
})();

;(() => {
  if (document.getElementById("qv4-chat-dark-style")) return;
  const s = document.createElement("style");
  s.id = "qv4-chat-dark-style";
  s.textContent = `
    .qv4-input-dark textarea::placeholder { color: rgba(255,255,255,0.6) !important; }
    .qv4-input-dark .ant-btn-text:hover { background: rgba(255,255,255,0.1) !important; }
    .qv4-input-dark .ant-btn-text:active { background: rgba(255,255,255,0.2) !important; }

    .qv4-dark-menu .ant-dropdown-menu-item,
    .qv4-dark-menu .ant-dropdown-menu-item a { color: #FAFAFA !important; }
    .qv4-dark-menu .ant-dropdown-menu-item:hover { background: rgba(255,255,255,0.1) !important; }
    .qv4-dark-menu .ant-dropdown-menu-item-disabled,
    .qv4-dark-menu .ant-dropdown-menu-item-disabled:hover { background: transparent !important; }
    .qv4-dark-menu .ant-dropdown-menu-item-divider { background-color: rgba(255,255,255,0.1) !important; }
  `;
  document.head.appendChild(s);
})();

// ── Message delivery status tick ──────────────────────────────────────────────
export function MsgStatus({ status, listPreview = false }) {
  if (listPreview && ["PENDING", "QUEUED", "PROCESSING", "SUBMITTED"].includes(status)) status = "SENDING";
  const isDark = useContext(ChatThemeCtx);
  const prevRef = React.useRef(status);
  const [glowing, setGlowing] = React.useState(false);

  React.useEffect(() => {
    if (prevRef.current !== status) {
      prevRef.current = status;
      setGlowing(true);
      const t = setTimeout(() => setGlowing(false), 600);
      return () => clearTimeout(t);
    }
  }, [status]);

  if (!status) return null;

  const glyph = STATUS_GLYPHS[status];
  if (!glyph) return null;
  const grey = isDark ? "#8696a0" : "#8c8c8c";
  const color = status === "READ" ? (isDark ? "#53BDEB" : "#34B7F1")
    : status === "FAILED" ? (isDark ? "#FB5061" : "#ff4d4f")
    : grey;
  return (
    <span role="img" aria-label={glyph.label} title={glyph.label} data-status={status}
      style={{ display: "inline-flex", alignItems: "center", marginLeft: 3, color, opacity: glyph.opacity ?? 1,
        verticalAlign: "-1px", lineHeight: 0, ...(glowing ? { animation: "msgStatusGlow 0.6s ease-out" } : null) }}>
      <svg width="16" height="11" viewBox="0 0 16 11" fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{glyph.draw}</svg>
    </span>
  );
}

// Drawn 16×11 delivery glyphs (WhatsApp-style shapes, our own paths): one
// stroke weight for every state so ticks, clock and failure line up.
const SINGLE_TICK = <path d="M3 5.8l2.7 2.7L11.4 2.2" />;
const STATUS_GLYPHS = {
  SENDING: { label: "Sending", draw: <><circle cx="8" cy="5.5" r="4.3" strokeWidth="1.2" /><path d="M8 3.2v2.5l1.6 1" strokeWidth="1.2" /></> },
  SUBMITTED: { label: "Sent to provider — delivery not confirmed", opacity: 0.55, draw: SINGLE_TICK },
  SENT: { label: "Sent", draw: SINGLE_TICK },
  DELIVERED: { label: "Delivered", draw: <><path d="M1 5.8l2.7 2.7L9.4 2.2" /><path d="M7.6 7.9l.6.6L13.9 2.2" /></> },
  READ: { label: "Read", draw: <><path d="M1 5.8l2.7 2.7L9.4 2.2" /><path d="M7.6 7.9l.6.6L13.9 2.2" /></> },
  FAILED: { label: "Not delivered", draw: <><circle cx="8" cy="5.5" r="4.8" fill="currentColor" stroke="none" /><path d="M8 3.2v2.6" stroke="#fff" strokeWidth="1.4" /><circle cx="8" cy="7.9" r="0.75" fill="#fff" stroke="none" /></> },
};

// ── Shared contexts ───────────────────────────────────────────────────────────
// Chat panel theme — true = dark (WhatsApp-style), false = light
export const ChatThemeCtx = React.createContext(true);
// Whose chat this is: "customer" (WhatsApp green) or "supplier" (the same
// layout in cool blues, as on the phone), so the two never look alike.
export const ChatAudienceCtx = React.createContext("customer");

// ── Theme color map ───────────────────────────────────────────────────────────
// Supplier chats keep every customer colour except their own blues.
const SUPPLIER_TONES = {
  dark: {
    bg: "#0E1520",
    surface: "#18212E",
    border: "rgba(160,190,255,0.14)",
    pill: "#18212E",
    bubbleIn: "#1F2A3A",
    bubbleOut: "#1E3F6E",
    input: "#1F2A3A",
    tray: "#0E1520",
    accent: "#6F9BFF",
    onAccent: "#0A0A0A",
    accentText: "#6F9BFF",
    action: "rgba(111,155,255,0.15)",
    onAction: "#6F9BFF",
    link: "#7FB2FF",
    mapLink: "#7FB2FF",
    textSub: "rgba(225,235,255,0.62)",
  },
  light: {
    surface: "#eef2f7",
    border: "#cdd7e3",
    // Same saturation and lightness as the customers' #d9fdd3.
    bubbleOut: "#d3e5fd",
    tray: "#eef2f7",
    accent: "#2f6bd8",
    onAccent: "#ffffff",
    accentText: "#1f55b3",
    action: "#2f6bd8",
    noteCount: "#1f55b3",
    link: "#1558b0",
    mapLink: "#1558b0",
    textSub: "#4f5f69",
  },
};

export function ct(isDark, audience = "customer") {
  const base = customerTheme(isDark);
  const theme = audience === "supplier" ? { ...base, ...SUPPLIER_TONES[isDark ? "dark" : "light"] } : base;
  // The list header and a thread's header, tabs and composer: tinted with
  // the audience's colour in light mode; dark mode keeps its plain surface.
  return { ...theme, unreadBadge: CHAT_UNREAD_BADGE.background, onUnreadBadge: CHAT_UNREAD_BADGE.foreground, chrome: isDark ? theme.surface : `color-mix(in srgb, ${theme.accent} 18%, ${theme.surface})` };
}

// The theme of the chat around the caller: light or dark, customer or supplier.
export function useChatTheme() {
  const isDark = useContext(ChatThemeCtx);
  const audience = useContext(ChatAudienceCtx);
  return React.useMemo(() => ct(isDark, audience), [isDark, audience]);
}

function customerTheme(isDark) {
  return isDark ? {
    bg: sharedDarkTokens.colorBgLayout,
    surface: sharedDarkTokens.colorBgElevated,
    border: sharedDarkTokens.colorBorderSecondary,
    pill: sharedDarkTokens.colorBgElevated,
    bubbleIn: sharedDarkTokens.colorBgContainer,
    bubbleOut: "#144D37",
    mediaCardBg: "#ffffff0c",
    mediaCardBorder: "#ffffff18",
    documentIcon: "#a4c9d6",
    pdfIcon: "#ff8585",
    mapLink: "#53bdeb",
    text: sharedDarkTokens.colorText,
    textSub: sharedDarkTokens.colorTextSecondary,
    input: sharedDarkTokens.colorBgContainer,
    tray: sharedDarkTokens.colorBgElevated,
    accent: "#21C063",
    onAccent: "#0A0A0A",
    // Roles. Dark mode keeps its current look: text in the accent, see-through pills.
    accentText: "#21C063",
    action: "rgba(33,192,99,0.15)",
    onAction: "#21C063",
    pillFace: null,
    noteText: "#08979c",
    noteFill: "#08979c24",
    noteCount: "#f5222d",
    tagText: "#9254de",
    tagFill: "#9254de24",
    negativeFill: "rgba(251,80,97,0.15)",
    link: "#21C063",
    negative: "#FB5061",
    warning: "#FFD279",
    bgFilter: "invert(1) brightness(0.65) contrast(0.65) saturate(0.25)",
    bgOpacity: 0.28,
  } : {
    bg: "#eae6df",
    surface: "#f0f2f5",
    border: "#d1d7db",
    pill: "#dfe5e7",
    bubbleIn: "#ffffff",
    bubbleOut: "#d9fdd3",
    // Cards inside bubbles (documents, links, quotes) have a solid neutral
    // face, so they read the same in green, blue and white bubbles.
    mediaCardBg: "#f0f2f5",
    mediaCardBorder: "#d1d7db",
    documentIcon: "#416b7d",
    pdfIcon: "#c93b3b",
    mapLink: "#007eaa",
    text: "#111b21",
    // 4.5:1 or better on the tinted chrome, white and the outgoing bubble.
    textSub: "#4f5f69",
    input: "#ffffff",
    tray: "#f0f2f5",
    accent: "#00a884",
    onAccent: "#ffffff",
    // Roles: coloured text, the main action's fill, solid pill faces.
    accentText: "#006b56",
    action: "#008069",
    onAction: "#ffffff",
    pillFace: "#ffffff",
    // Notes and tags are neutral; only the note count carries colour.
    noteText: "#4f5f69",
    noteFill: "#ffffff",
    noteCount: "#006b56",
    tagText: "#4f5f69",
    tagFill: "#ffffff",
    negativeFill: "#ffffff",
    link: "#00a884",
    negative: "#cf1322",
    warning: "#875400",
    bgFilter: "brightness(0.4) contrast(0.6) saturate(0.35)",
    bgOpacity: 0.07,
  };
}
